/**
 * QuickMatchPage.jsx
 * 
 * Timed "10-second decisions" Quick Match experience.
 * Wraps profile card display with countdown timer and auto-skip logic.
 * 
 * This is separate from /discover (untimed browsing) to preserve stability
 * while restoring the original gamified Quick Match experience.
 * 
 * Timer Lifecycle Safety:
 * - Single interval at a time (stopTimer called before startTimer)
 * - Cleanup on unmount/route change
 * - Guards against double auto-skip and decision races
 * - Timer pauses for all modal overlays
 * - Timer pauses when Quick Filters UI is open
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, X, Clock, Sparkles, Eye, SlidersHorizontal, RotateCcw } from "lucide-react";
import apiClient from "../api/client";
import ProfileAvatar from "../components/ProfileAvatar";
import ProfileMediaGallery from "../components/ProfileMediaGallery";
import { ProfileViewModal } from "../components/profile/ProfileViewModal";
import { useProfileViewer } from "../hooks/useProfileViewer";
import { markFlowStep } from "../utils/unityFlowStorage";
import { track } from "../utils/analytics";
import { safeArr, isCouple } from "../utils/safeProfile";

const API = window.location.origin;
const TIMER_DURATION = 10; // seconds per profile
const FILTERS_STORAGE_KEY = "ul_quickmatch_filters_v1";

// Filter options
const GENDER_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "man", label: "Men" },
  { value: "woman", label: "Women" },
  { value: "nonbinary", label: "Nonbinary" },
  { value: "couples", label: "Couples" },
];

const RELATIONSHIP_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "monogamous", label: "Monogamous" },
  { value: "polyamorous", label: "Poly" },
  { value: "open", label: "Open" },
  { value: "exploring", label: "Exploring" },
];

// Intent Signal options (matches backend enum)
const INTENT_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "serious", label: "Serious" },
  { value: "friends_first", label: "Friends-first" },
  { value: "casual", label: "Casual" },
  { value: "exploring", label: "Exploring" },
];

// Valid intent values for migration
const VALID_INTENT_VALUES = new Set(["serious", "friends_first", "casual", "exploring"]);

// Load filters from localStorage with migration for vibe -> intent
const loadSavedFilters = () => {
  try {
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      
      // Migrate old "vibe" to new "intent" field
      let intentValue = "any";
      if (parsed.intent && VALID_INTENT_VALUES.has(parsed.intent)) {
        // New format - use as-is
        intentValue = parsed.intent;
      } else if (parsed.vibe) {
        // Old format - migrate if value matches new enum
        // Map old hyphenated "friends-first" to new underscore "friends_first"
        const migratedVibe = parsed.vibe === "friends-first" ? "friends_first" : parsed.vibe;
        if (VALID_INTENT_VALUES.has(migratedVibe)) {
          intentValue = migratedVibe;
        }
        // else: unknown vibe value, reset to "any"
      }
      
      return {
        gender: parsed.gender || "any",
        relationshipStyle: parsed.relationshipStyle || "any",
        intent: intentValue,
      };
    }
  } catch (e) {
    console.warn("[QuickMatch] Failed to load saved filters:", e);
  }
  return { gender: "any", relationshipStyle: "any", intent: "any" };
};

// Save filters to localStorage
const saveFilters = (filters) => {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (e) {
    console.warn("[QuickMatch] Failed to save filters:", e);
  }
};

export default function QuickMatchPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Profile data
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sessionEnded, setSessionEnded] = useState(false);

  // Action states
  const [likeLoading, setLikeLoading] = useState(false);
  const [passLoading, setPassLoading] = useState(false);

  // Session tracking
  const [sessionLikes, setSessionLikes] = useState(0);
  const [sessionPasses, setSessionPasses] = useState(0);
  const [sessionTimeouts, setSessionTimeouts] = useState(0);
  const sessionStartedRef = useRef(false);

  // Timer states
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION);
  const [timerActive, setTimerActive] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const timerRef = useRef(null);
  const isAutoSkippingRef = useRef(false); // Guard against double auto-skip
  const isDecidingRef = useRef(false); // Guard against concurrent decisions

  // Quick Filters state
  const [filters, setFilters] = useState(loadSavedFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState(null); // Temp state while sheet is open
  const [noMatchesForFilters, setNoMatchesForFilters] = useState(false);

  // Profile viewer modal
  const profileViewer = useProfileViewer();

  // Check if any filter is active (not "any")
  const hasActiveFilters = filters.gender !== "any" || 
                           filters.relationshipStyle !== "any" || 
                           filters.intent !== "any";

  // Filter matching function
  // NOTE: Filters match the TARGET profile's attributes, not the current user's
  const profileMatchesFilters = useCallback((profile, activeFilters) => {
    if (!profile) return false;
    
    // Gender filter logic (uses shared isCouple helper from safeProfile.js)
    if (activeFilters.gender !== "any") {
      if (activeFilters.gender === "couples") {
        // Only show couples
        if (!isCouple(profile)) return false;
      } else {
        // Show singles matching the gender, exclude couples
        if (isCouple(profile)) return false;
        
        const profileGender = (profile.gender || "").toLowerCase();
        // Normalize variations: "male" -> "man", "female" -> "woman"
        const normalizedGender = profileGender === "male" ? "man" 
                               : profileGender === "female" ? "woman" 
                               : profileGender;
        if (normalizedGender !== activeFilters.gender) {
          return false;
        }
      }
    }
    
    // Relationship style filter - matches target's style
    if (activeFilters.relationshipStyle !== "any") {
      const profileStyle = (profile.relationship_style || "").toLowerCase();
      // If profile has no relationship_style set, include them (don't exclude unknowns)
      if (profileStyle && profileStyle !== activeFilters.relationshipStyle) {
        return false;
      }
    }
    
    // Intent filter - matches target's intent_signal
    // Missing intent_signal should only appear when filter = Any
    if (activeFilters.intent !== "any") {
      const profileIntent = (profile.intent_signal || "").toLowerCase();
      // If profile has no intent_signal set, exclude them when filtering
      if (!profileIntent || profileIntent !== activeFilters.intent) {
        return false;
      }
    }
    
    return true;
  }, []);

  // Get filtered profiles list
  const filteredProfiles = useMemo(() => {
    if (!hasActiveFilters) return profiles;
    return profiles.filter(p => profileMatchesFilters(p, filters));
  }, [profiles, filters, hasActiveFilters, profileMatchesFilters]);

  // Current profile helper (uses filtered list)
  const currentProfile = filteredProfiles[currentIndex] || null;
  const hasMoreProfiles = currentIndex < filteredProfiles.length && filteredProfiles.length > 0;
  const profileUserId = currentProfile?.user_id || currentProfile?.id;

  // Helper: generate filters hash for analytics
  const filtersHash = useMemo(() => {
    return `${filters.gender}|${filters.relationshipStyle}|${filters.intent}`;
  }, [filters]);

  // Fetch profiles on mount
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setLoadError(null);
        console.info("[QuickMatch] Fetching profiles...");

        const headers = {};
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await apiClient.get(`/profiles/discover`, { headers });
        const items = Array.isArray(response?.data) ? response.data : [];

        console.info(`[QuickMatch] Loaded ${items.length} profiles`);
        setProfiles(items);
      } catch (err) {
        console.error("[QuickMatch] Failed to fetch profiles:", err);
        setLoadError(err.message || "Failed to load profiles");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchProfiles();
    } else {
      setLoading(false);
      setLoadError("Please log in to use Quick Match");
    }
  }, [token]);

  // Timer management - MUST be defined before useEffects that use them
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setTimerActive(false);
  }, []);

  const startTimer = useCallback(() => {
    // Don't start if session has ended
    if (sessionEnded) return;
    
    // Always stop first to prevent stacking
    stopTimer();
    
    setTimeLeft(TIMER_DURATION);
    setTimerActive(true);
    setShowTimeWarning(false);
    isAutoSkippingRef.current = false;

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setTimerActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [stopTimer, sessionEnded]);

  // Check for no matches after filters change
  useEffect(() => {
    if (!loading && profiles.length > 0) {
      const matchingCount = filteredProfiles.length;
      if (matchingCount === 0 && hasActiveFilters) {
        setNoMatchesForFilters(true);
        stopTimer();
      } else {
        setNoMatchesForFilters(false);
      }
    }
  }, [loading, profiles.length, filteredProfiles.length, hasActiveFilters, stopTimer]);

  // Record profile shown event (for flirt timer analytics)
  useEffect(() => {
    if (currentProfile && profileUserId && token && !loading && !filtersOpen) {
      apiClient.post(`/flirt-timer/show/${profileUserId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(err => {
        console.warn("[QuickMatch] Failed to record show event:", err);
      });
    }
  }, [currentIndex, currentProfile, profileUserId, token, loading, filtersOpen]);

  // Track session start (once)
  useEffect(() => {
    if (!loading && profiles.length > 0 && !sessionStartedRef.current) {
      sessionStartedRef.current = true;
      track("quick_match_session_start", {
        profile_count: profiles.length,
        filtered_count: filteredProfiles.length,
        filters_active: hasActiveFilters,
        filters_hash: filtersHash,
        filters: hasActiveFilters ? filters : null,
      });
    }
  }, [loading, profiles.length, filteredProfiles.length, hasActiveFilters, filters, filtersHash]);

  // Cleanup on unmount or route change - CRITICAL
  useEffect(() => {
    return () => {
      // Stop any running timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      // Track session end if session was started
      if (sessionStartedRef.current) {
        track("quick_match_session_end", {
          likes: sessionLikes,
          passes: sessionPasses,
          timeouts: sessionTimeouts,
          profiles_seen: currentIndex,
          completed: sessionEnded,
          filters_active: hasActiveFilters,
          filters_hash: filtersHash,
        });
      }
    };
  }, [sessionLikes, sessionPasses, sessionTimeouts, currentIndex, sessionEnded, hasActiveFilters, filtersHash]);

  // Start timer when profile changes (only if deck not exhausted and no overlays open)
  useEffect(() => {
    // Guard: Don't start timer if any overlay is open (filters, profile modal, end screen)
    const overlayOpen = filtersOpen || profileViewer.isOpen;
    
    if (hasMoreProfiles && !loading && !sessionEnded && !overlayOpen && !noMatchesForFilters) {
      startTimer();
    } else if (!hasMoreProfiles && !loading && filteredProfiles.length > 0 && !noMatchesForFilters) {
      // Deck exhausted - end session
      stopTimer();
      setSessionEnded(true);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [currentIndex, hasMoreProfiles, loading, startTimer, stopTimer, filteredProfiles.length, sessionEnded, filtersOpen, noMatchesForFilters, profileViewer.isOpen]);

  // Warning state when time is low
  useEffect(() => {
    if (timeLeft <= 3 && timeLeft > 0) {
      setShowTimeWarning(true);
    } else {
      setShowTimeWarning(false);
    }
  }, [timeLeft]);

  // Unified deck advancement (single point of truth)
  const advanceToNextProfile = useCallback(() => {
    const nextIndex = currentIndex + 1;
    const isLastProfile = nextIndex >= filteredProfiles.length;
    
    // Release guards
    isDecidingRef.current = false;
    isAutoSkippingRef.current = false;
    
    if (isLastProfile) {
      // Deck exhausted - stop timer, end session
      stopTimer();
      setSessionEnded(true);
    }
    
    setCurrentIndex(nextIndex);
  }, [currentIndex, filteredProfiles.length, stopTimer]);

  // Auto-skip handler (defined before useEffect that uses it)
  const handleAutoSkip = useCallback(async () => {
    // One-shot guard: prevent double auto-skip
    if (isAutoSkippingRef.current || isDecidingRef.current || sessionEnded) {
      return;
    }
    isAutoSkippingRef.current = true;

    // Track timeout decision
    track("quick_match_decision", {
      type: "timeout",
      time_left: 0,
      profile_index: currentIndex,
      filters_hash: filtersHash,
    });

    if (!profileUserId) {
      advanceToNextProfile();
      return;
    }

    // Record timeout event (fire-and-forget, don't block UX)
    apiClient.post(`/flirt-timer/timeout/${profileUserId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(err => {
      console.warn("[QuickMatch] Failed to record timeout:", err);
    });

    setSessionTimeouts(prev => prev + 1);
    advanceToNextProfile();
  }, [profileUserId, token, advanceToNextProfile, sessionEnded, currentIndex, filtersHash]);

  // Auto-skip on timeout
  useEffect(() => {
    if (timeLeft === 0 && timerActive === false && hasMoreProfiles && !isAutoSkippingRef.current && !isDecidingRef.current && !sessionEnded) {
      handleAutoSkip();
    }
  }, [timeLeft, timerActive, hasMoreProfiles, handleAutoSkip, sessionEnded]);

  const handleLike = useCallback(async () => {
    // Guard against concurrent decisions or auto-skip race
    if (!profileUserId || likeLoading || isDecidingRef.current || isAutoSkippingRef.current || sessionEnded) return;
    
    isDecidingRef.current = true;
    const decisionTimeLeft = timeLeft; // Capture before stopping
    stopTimer();
    setLikeLoading(true);

    // Track decision with time context
    track("quick_match_decision", {
      type: "like",
      time_left: decisionTimeLeft,
      profile_index: currentIndex,
      filters_hash: filtersHash,
    });

    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`/matches/like/${encodeURIComponent(profileUserId)}`, {
        method: "POST",
        headers,
      });

      if (res.ok) {
        console.info("[QuickMatch] Like successful for:", profileUserId);
        setSessionLikes(prev => prev + 1);
        markFlowStep('sentFlirt');
      } else {
        console.error("[QuickMatch] Like failed:", res.status);
      }
    } catch (err) {
      console.error("[QuickMatch] handleLike error:", err);
    } finally {
      setLikeLoading(false);
      advanceToNextProfile();
    }
  }, [profileUserId, likeLoading, token, stopTimer, advanceToNextProfile, sessionEnded, timeLeft, currentIndex, filtersHash]);

  const handlePass = useCallback(async () => {
    // Guard against concurrent decisions or auto-skip race
    if (!profileUserId || passLoading || isDecidingRef.current || isAutoSkippingRef.current || sessionEnded) return;
    
    isDecidingRef.current = true;
    const decisionTimeLeft = timeLeft; // Capture before stopping
    stopTimer();
    setPassLoading(true);

    // Track decision with time context
    track("quick_match_decision", {
      type: "pass",
      time_left: decisionTimeLeft,
      profile_index: currentIndex,
      filters_hash: filtersHash,
    });

    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`/matches/pass/${encodeURIComponent(profileUserId)}`, {
        method: "POST",
        headers,
      });

      if (res.ok) {
        console.info("[QuickMatch] Pass successful for:", profileUserId);
        setSessionPasses(prev => prev + 1);
        markFlowStep('sentFlirt');
      } else {
        console.error("[QuickMatch] Pass failed:", res.status);
      }
    } catch (err) {
      console.error("[QuickMatch] handlePass error:", err);
    } finally {
      setPassLoading(false);
      advanceToNextProfile();
    }
  }, [profileUserId, passLoading, token, stopTimer, advanceToNextProfile, sessionEnded, timeLeft, currentIndex, filtersHash]);

  const handleViewProfile = () => {
    if (currentProfile) {
      stopTimer();
      profileViewer.showProfile(currentProfile);
    }
  };

  // Quick Filters handlers
  const openFilters = useCallback(() => {
    stopTimer(); // CRITICAL: Stop timer when filters open
    setPendingFilters({ ...filters });
    setFiltersOpen(true);
  }, [stopTimer, filters]);

  const closeFilters = useCallback((apply = false) => {
    if (apply && pendingFilters) {
      // Apply new filters
      const newFilters = { ...pendingFilters };
      setFilters(newFilters);
      saveFilters(newFilters);
      
      // Track filter change with hash
      const newFiltersHash = `${newFilters.gender}|${newFilters.relationshipStyle}|${newFilters.intent}`;
      track("quick_match_filters_changed", {
        gender: newFilters.gender,
        relationship_style: newFilters.relationshipStyle,
        intent_signal: newFilters.intent,
        filters_hash: newFiltersHash,
      });
      
      // Reset session for new filters (UX note: this starts a fresh session)
      setCurrentIndex(0);
      setSessionEnded(false);
      isDecidingRef.current = false;
      isAutoSkippingRef.current = false;
    }
    
    setFiltersOpen(false);
    setPendingFilters(null);
    // Timer will restart via the useEffect when next profile renders
  }, [pendingFilters]);

  const resetFilters = useCallback(() => {
    const defaultFilters = { gender: "any", relationshipStyle: "any", intent: "any" };
    setFilters(defaultFilters);
    saveFilters(defaultFilters);
    setNoMatchesForFilters(false);
    setCurrentIndex(0);
    setSessionEnded(false);
    isDecidingRef.current = false;
    isAutoSkippingRef.current = false;
    
    track("quick_match_filters_reset", { previous_filters_hash: filtersHash });
  }, [filtersHash]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-rose-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading Quick Match...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Couldn&apos;t Load Quick Match</h2>
          <p className="text-slate-400 mb-6">{loadError}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-rose-500 text-white rounded-full hover:bg-rose-600 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // No matches for current filters state
  if (noMatchesForFilters) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <SlidersHorizontal className="w-10 h-10 text-slate-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">No Matches for These Filters</h2>
          <p className="text-slate-400 mb-6">
            Try adjusting your filters to see more profiles.
          </p>
          
          {/* Current filters display */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6 text-left">
            <p className="text-xs text-slate-500 mb-2">Current filters:</p>
            <div className="flex flex-wrap gap-2">
              {filters.gender !== "any" && (
                <span className="px-2 py-1 bg-slate-700 rounded-full text-xs text-slate-300">
                  {GENDER_OPTIONS.find(o => o.value === filters.gender)?.label}
                </span>
              )}
              {filters.relationshipStyle !== "any" && (
                <span className="px-2 py-1 bg-slate-700 rounded-full text-xs text-slate-300">
                  {RELATIONSHIP_OPTIONS.find(o => o.value === filters.relationshipStyle)?.label}
                </span>
              )}
              {filters.intent !== "any" && (
                <span className="px-2 py-1 bg-slate-700 rounded-full text-xs text-slate-300">
                  {INTENT_OPTIONS.find(o => o.value === filters.intent)?.label}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={resetFilters}
              className="w-full px-6 py-3 bg-rose-500 text-white rounded-full hover:bg-rose-600 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset Filters
            </button>
            <button
              onClick={() => navigate('/discover')}
              className="w-full px-6 py-3 bg-slate-800 text-slate-300 rounded-full hover:bg-slate-700 transition-colors font-medium"
            >
              Browse Instead (No Timer)
            </button>
          </div>
        </div>
      </div>
    );
  }

  // End of deck state
  if (!hasMoreProfiles) {
    const totalDecisions = sessionLikes + sessionPasses + sessionTimeouts;

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Session Complete!</h2>
          <p className="text-slate-400 mb-6">You&apos;ve made {totalDecisions} decisions this session.</p>

          {/* Session stats */}
          <div className="flex justify-center gap-6 mb-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-rose-400">{sessionLikes}</div>
              <div className="text-xs text-slate-500">Likes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-400">{sessionPasses}</div>
              <div className="text-xs text-slate-500">Passes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-400">{sessionTimeouts}</div>
              <div className="text-xs text-slate-500">Timeouts</div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => {
                setCurrentIndex(0);
                setProfiles([]);
                setLoading(true);
                setSessionLikes(0);
                setSessionPasses(0);
                setSessionTimeouts(0);
                setSessionEnded(false);
                sessionStartedRef.current = false;
                // Trigger re-fetch
                window.location.reload();
              }}
              className="w-full px-6 py-3 bg-rose-500 text-white rounded-full hover:bg-rose-600 transition-colors font-medium"
            >
              Play Again
            </button>
            <button
              onClick={() => {
                track("quick_match_to_browse_clicked", { from: "session_end" });
                navigate('/discover');
              }}
              className="w-full px-6 py-3 bg-slate-800 text-slate-300 rounded-full hover:bg-slate-700 transition-colors font-medium"
            >
              Browse Instead (No Timer)
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-6 py-2 text-slate-500 hover:text-slate-300 transition-colors text-sm"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Quick Match UI
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <nav className="sticky top-0 z-40 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                track("quick_match_to_browse_clicked", { from: "header" });
                navigate('/discover');
              }}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors underline"
            >
              Browse instead
            </button>
          </div>

          <div className="text-center">
            <h1 className="text-lg font-semibold text-white">Quick Match</h1>
            <p className="text-xs text-rose-400">10-second decisions</p>
          </div>

          {/* Timer display */}
          <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
            showTimeWarning 
              ? 'bg-red-500/20 text-red-400 animate-pulse' 
              : 'bg-slate-800 text-slate-300'
          }`}>
            <Clock className="w-4 h-4" />
            <span className="font-mono font-semibold">{timeLeft}s</span>
          </div>
        </div>

        {/* Timer progress bar */}
        {timerActive && (
          <div className="h-1 bg-slate-800">
            <div 
              className={`h-full transition-all duration-1000 ease-linear ${
                showTimeWarning ? 'bg-red-500' : 'bg-rose-500'
              }`}
              style={{ width: `${(timeLeft / TIMER_DURATION) * 100}%` }}
            />
          </div>
        )}
      </nav>

      {/* Quick Filters Bar */}
      <div className="bg-slate-900/80 border-b border-slate-800 px-4 py-2">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <button
            onClick={openFilters}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 transition-colors text-sm text-slate-300"
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="w-5 h-5 rounded-full bg-rose-500 text-white text-xs flex items-center justify-center">
                {[filters.gender !== "any", filters.relationshipStyle !== "any", filters.intent !== "any"].filter(Boolean).length}
              </span>
            )}
          </button>
          
          {/* Active filter chips */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2">
              <div className="flex gap-1 overflow-x-auto max-w-[200px]">
                {filters.gender !== "any" && (
                  <span className="px-2 py-0.5 bg-slate-700 rounded-full text-xs text-slate-300 whitespace-nowrap">
                    {GENDER_OPTIONS.find(o => o.value === filters.gender)?.label}
                  </span>
                )}
                {filters.relationshipStyle !== "any" && (
                  <span className="px-2 py-0.5 bg-slate-700 rounded-full text-xs text-slate-300 whitespace-nowrap">
                    {RELATIONSHIP_OPTIONS.find(o => o.value === filters.relationshipStyle)?.label}
                  </span>
                )}
                {filters.intent !== "any" && (
                  <span className="px-2 py-0.5 bg-slate-700 rounded-full text-xs text-slate-300 whitespace-nowrap">
                    {INTENT_OPTIONS.find(o => o.value === filters.intent)?.label}
                  </span>
                )}
              </div>
              <button
                onClick={resetFilters}
                className="p-1 text-slate-500 hover:text-slate-300 transition-colors"
                title="Reset filters"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Quick Filters Sheet */}
      {filtersOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/60 z-50"
            onClick={() => closeFilters(false)}
          />
          
          {/* Sheet */}
          <div className="fixed bottom-0 left-0 right-0 bg-slate-900 rounded-t-3xl z-50 max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              {/* Handle */}
              <div className="w-12 h-1 bg-slate-700 rounded-full mx-auto mb-6" />
              
              <h2 className="text-xl font-semibold text-white mb-2">Quick Filters</h2>
              <p className="text-sm text-slate-400 mb-6">
                Timer is paused while you adjust filters.
                <span className="block text-xs text-slate-500 mt-1">Applying filters starts a new session.</span>
              </p>
              
              {/* Gender Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-3">
                  Who to see
                </label>
                <div className="flex flex-wrap gap-2">
                  {GENDER_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setPendingFilters(prev => ({ ...prev, gender: opt.value }))}
                      className={`px-4 py-2 rounded-full text-sm transition-colors ${
                        pendingFilters?.gender === opt.value
                          ? 'bg-rose-500 text-white'
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Relationship Style Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-3">
                  Relationship Style
                </label>
                <div className="flex flex-wrap gap-2">
                  {RELATIONSHIP_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setPendingFilters(prev => ({ ...prev, relationshipStyle: opt.value }))}
                      className={`px-4 py-2 rounded-full text-sm transition-colors ${
                        pendingFilters?.relationshipStyle === opt.value
                          ? 'bg-rose-500 text-white'
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Intent Filter */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-slate-300 mb-3">
                  Intent
                </label>
                <div className="flex flex-wrap gap-2">
                  {INTENT_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setPendingFilters(prev => ({ ...prev, intent: opt.value }))}
                      className={`px-4 py-2 rounded-full text-sm transition-colors ${
                        pendingFilters?.intent === opt.value
                          ? 'bg-rose-500 text-white'
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => closeFilters(false)}
                  className="flex-1 px-6 py-3 bg-slate-800 text-slate-300 rounded-full hover:bg-slate-700 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => closeFilters(true)}
                  className="flex-1 px-6 py-3 bg-rose-500 text-white rounded-full hover:bg-rose-600 transition-colors font-medium"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Urgency banner */}
      {showTimeWarning && (
        <div className="bg-red-500/10 border-b border-red-500/20 px-4 py-2">
          <div className="max-w-md mx-auto flex items-center justify-center gap-2 text-red-400 text-sm font-medium">
            <Clock className="w-4 h-4 animate-bounce" />
            <span>Decide quickly! {timeLeft}s left</span>
          </div>
        </div>
      )}

      {/* Profile Card */}
      <div className="max-w-md mx-auto px-4 py-6">
        <div 
          className={`rounded-3xl overflow-hidden transition-all duration-300 ${
            showTimeWarning 
              ? 'ring-4 ring-red-500/50 shadow-lg shadow-red-500/20' 
              : 'ring-1 ring-slate-800'
          }`}
          style={{ background: '#0F1120' }}
        >
          {/* Profile Media */}
          <div className="relative">
            <ProfileMediaGallery
              profile={currentProfile}
              className="aspect-[3/4]"
              onClick={handleViewProfile}
            />

            {/* View profile overlay */}
            <button
              onClick={handleViewProfile}
              className="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full text-white text-sm hover:bg-black/80 transition-colors"
            >
              <Eye className="w-4 h-4" />
              <span>View Profile</span>
            </button>
          </div>

          {/* Profile Info */}
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h2 className="text-xl font-semibold text-white">
                  {currentProfile?.display_name || 
                   (currentProfile?.names && currentProfile.names[0]) || 
                   "Someone"}
                </h2>
                {currentProfile?.location_city && (
                  <p className="text-sm text-slate-400">
                    {currentProfile.location_city}
                    {currentProfile.location_region && `, ${currentProfile.location_region}`}
                  </p>
                )}
                {/* Intent Signal Badge - fixed height container to prevent layout shift */}
                <div className="h-6 mt-1">
                  {currentProfile?.intent_signal && (
                    <span 
                      className="inline-flex items-center rounded-full bg-purple-500/20 border border-purple-500/40 px-2.5 py-0.5 text-xs font-medium text-purple-200"
                      data-testid="quick-match-intent-badge"
                    >
                      {currentProfile.intent_signal === "serious" ? "Serious" :
                       currentProfile.intent_signal === "friends_first" ? "Friends-first" :
                       currentProfile.intent_signal === "casual" ? "Casual" :
                       currentProfile.intent_signal === "exploring" ? "Exploring" : null}
                    </span>
                  )}
                </div>
              </div>
              <ProfileAvatar
                profile={currentProfile}
                size="sm"
                className="ring-2 ring-slate-700"
              />
            </div>

            {currentProfile?.bio && (
              <p className="text-sm text-slate-300 line-clamp-2 mb-4">
                {currentProfile.bio}
              </p>
            )}

            {/* Action buttons */}
            <div className="flex items-center justify-center gap-4">
              {/* Pass button */}
              <button
                onClick={handlePass}
                disabled={passLoading || likeLoading}
                className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                  showTimeWarning
                    ? 'bg-slate-700 hover:bg-slate-600'
                    : 'bg-slate-800 hover:bg-slate-700'
                } text-slate-300 hover:text-white disabled:opacity-50`}
                data-testid="quick-match-pass-btn"
              >
                {passLoading ? (
                  <div className="w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <X className="w-8 h-8" />
                )}
              </button>

              {/* Like button */}
              <button
                onClick={handleLike}
                disabled={likeLoading || passLoading}
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                  showTimeWarning
                    ? 'bg-green-500 hover:bg-green-600 animate-pulse'
                    : 'bg-rose-500 hover:bg-rose-600'
                } text-white disabled:opacity-50`}
                data-testid="quick-match-like-btn"
              >
                {likeLoading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Heart className="w-10 h-10 fill-current" />
                )}
              </button>
            </div>

            {/* Progress indicator */}
            <div className="mt-4 text-center">
              <p className="text-xs text-slate-500">
                {currentIndex + 1} of {profiles.length} profiles
              </p>
              {timerActive && (
                <p className="text-xs text-slate-600 mt-1">
                  Auto-skip in {timeLeft}s if no action
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Session stats */}
        <div className="mt-6 flex justify-center gap-8">
          <div className="text-center">
            <div className="text-xl font-bold text-rose-400">{sessionLikes}</div>
            <div className="text-xs text-slate-500">Likes</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-slate-400">{sessionPasses}</div>
            <div className="text-xs text-slate-500">Passes</div>
          </div>
        </div>
      </div>

      {/* Profile View Modal */}
      <ProfileViewModal
        open={profileViewer.open}
        onClose={() => {
          profileViewer.close();
          // Resume timer when modal closes
          if (hasMoreProfiles) {
            startTimer();
          }
        }}
        profile={profileViewer.profile}
        isSelf={false}
        source="quick-match"
        onLikeFromProfile={() => {
          profileViewer.close();
          handleLike();
        }}
        onPassFromProfile={() => {
          profileViewer.close();
          handlePass();
        }}
      />
    </div>
  );
}
